import { cn, Flex, Switch, SwitchProps } from '@bridebook/aura';
import { forwardRef, ElementRef, ReactNode, useId, PropsWithChildren, useMemo } from 'react';
import {
  LabelField,
  LabelFieldError,
  LabelFieldMain,
  LabelFieldSupporting,
} from '../../label-field/label-field';

type SwitchSize = 'standard' | 'sm';

type SwitchFieldProps = Omit<SwitchProps, 'error'> & {
  label: ReactNode;
  description?: ReactNode;
  error?: boolean | string;
  size?: SwitchSize;
  classNames?: {
    wrapper?: string;
    labelWrapper?: string;
    labelMain?: string;
    labelSupporting?: string;
    labelError?: string;
  };
};

export const SwitchField = forwardRef<ElementRef<typeof Switch>, SwitchFieldProps>(
  (
    {
      label,
      description,
      id: idFromProps,
      disabled = false,
      error = false,
      size = 'standard',
      classNames,
      ...props
    },
    ref,
  ) => {
    const defaultId = useId();
    const id = idFromProps ?? defaultId;

    const fieldId = `${id}-field`;
    const errorId = `${id}-error`;
    const supportingId = `${id}-supporting`;

    const hasErrorMessage = typeof error === 'string';

    const ariaDescribedby = useMemo(
      () =>
        [description ? supportingId : undefined, hasErrorMessage ? errorId : undefined]
          .filter(Boolean)
          .join(' ') || undefined,
      [description, supportingId, hasErrorMessage, errorId],
    );

    return (
      <Flex className={cn('gap-2', classNames?.wrapper)}>
        <SpacingWrapper size={size}>
          <Switch
            id={fieldId}
            ref={ref}
            disabled={disabled}
            aria-describedby={ariaDescribedby}
            {...props}
          />
        </SpacingWrapper>
        <LabelField
          size={size}
          disabled={disabled}
          fieldId={fieldId}
          supportingId={supportingId}
          errorId={errorId}
          className={classNames?.labelWrapper}>
          <LabelFieldMain className={classNames?.labelMain}>{label}</LabelFieldMain>
          {description && (
            <LabelFieldSupporting className={classNames?.labelSupporting}>
              {description}
            </LabelFieldSupporting>
          )}
          {hasErrorMessage && (
            <LabelFieldError className={classNames?.labelError}>{error}</LabelFieldError>
          )}
        </LabelField>
      </Flex>
    );
  },
);
SwitchField.displayName = 'SwitchField';

// Wrapper used for small checkbox.
// Needed to align the checkbox with the label,
// since the labels title line height is 20px.
const SpacingWrapper = ({
  children,
  size,
}: PropsWithChildren<{ size: SwitchFieldProps['size'] }>) => {
  if (size === 'sm') {
    return <div className="flex h-5 w-10 items-center justify-center">{children}</div>;
  }
  return <>{children}</>;
};
